/**
 * Mobile slider pagination.
 *
 * Wires a row of pill dots to any horizontally-scrolling container marked
 * with [data-mobile-pagination]. The dots row lives elsewhere in the same
 * section and is marked with [data-mobile-pagination-bar].
 *
 * Usage:
 *   <div data-mobile-pagination>...slides...</div>
 *   <div class="mobile-pagination" data-mobile-pagination-bar></div>
 *
 * Programmatic API (attached to the scroller element):
 *   scroller.__pagination.dots          -> array of dot elements
 *   scroller.__pagination.setActive(i)  -> highlight a specific dot
 *   scroller.__pagination.startTick(ms) -> animate active dot fill 0->1
 *   scroller.__pagination.stopTick()    -> reset active dot fill
 *   scroller.__pagination.scrollToIndex(i, behavior)
 */
(function () {
  if (window.__mobileSliderPaginationInit) return;
  window.__mobileSliderPaginationInit = true;

  var mobileQuery = window.matchMedia('(max-width: 768px)');

  function getSlides(scroller) {
    // Slides are direct element children, skipping <style>/<script> tags
    // that some sections interleave with their grid items, and any
    // decorative duplicate sets wrapped in aria-hidden containers.
    // Elements marked with `data-slide-skip` are also excluded — the
    // PDP collage uses this to keep its text-story block out of the
    // mobile image slider while keeping it in the desktop grid.
    var out = [];
    [].slice.call(scroller.children).forEach(function (child) {
      if (!child || child.nodeType !== 1) return;
      var tag = child.tagName;
      if (tag === 'STYLE' || tag === 'SCRIPT' || tag === 'TEMPLATE') return;
      if (child.getAttribute && child.getAttribute('aria-hidden') === 'true') return;
      if (child.hasAttribute && child.hasAttribute('data-slide-skip')) return;
      out.push(child);
    });
    return out;
  }

  function build(scroller) {
    if (scroller.__paginationBuilt) return;

    var sectionRoot = scroller.closest('[data-wetheme-section-id]')
                   || scroller.closest('[data-wetheme-section-type]')
                   || scroller.parentElement;
    var bar = sectionRoot && sectionRoot.querySelector('[data-mobile-pagination-bar]');
    if (!bar) return;

    var slides = getSlides(scroller);
    if (slides.length < 2) {
      bar.style.display = 'none';
      return;
    }

    scroller.__paginationBuilt = true;
    bar.innerHTML = '';

    var dots = [];
    slides.forEach(function (slide, i) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'mobile-pagination__dot';
      dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
      var track = document.createElement('span');
      track.className = 'mobile-pagination__dot-track';
      var fill = document.createElement('span');
      fill.className = 'mobile-pagination__dot-fill';
      track.appendChild(fill);
      dot.appendChild(track);
      dot.addEventListener('click', function () {
        scrollToIndex(i, 'smooth');
      });
      bar.appendChild(dot);
      dots.push(dot);
    });

    var active = -1;
    function clearFill(idx) {
      if (idx < 0 || !dots[idx]) return;
      var f = dots[idx].querySelector('.mobile-pagination__dot-fill');
      if (!f) return;
      f.style.transition = 'none';
      f.style.transform = 'scaleX(0)';
    }
    // Strip inline styles so the CSS active-state rule (scaleX(1)) can
    // re-apply. Without this, a previously-cleared dot stays empty when
    // it later becomes active again (the bug visible on swipe-back).
    function releaseFill(idx) {
      if (idx < 0 || !dots[idx]) return;
      var f = dots[idx].querySelector('.mobile-pagination__dot-fill');
      if (!f) return;
      f.style.transition = '';
      f.style.transform = '';
    }
    function setActive(i) {
      if (i < 0 || i >= dots.length) return;
      if (i === active) return;
      if (active >= 0 && dots[active]) {
        dots[active].classList.remove('mobile-pagination__dot--active');
        // Reset outgoing dot's autoplay fill immediately so it doesn't
        // linger as "full" after the slide advances.
        clearFill(active);
      }
      active = i;
      dots[i].classList.add('mobile-pagination__dot--active');
      // Clear any leftover inline style on the incoming dot so the
      // CSS active rule controls its fill. The reviews slider will
      // overwrite this again from startTick().
      releaseFill(i);
    }

    // Tap-to-swipe: on mobile, a tap on a non-active slide should center
    // it instead of activating its link. The link only fires when the
    // slide is already centered (active). This is captured at the bubble
    // phase so child anchors don't navigate.
    function onSlideClick(e) {
      if (!mobileQuery.matches) return;
      var slideEl = e.currentTarget;
      var idx = slides.indexOf(slideEl);
      if (idx < 0) return;
      if (idx === active) return; // active slide: let the link work
      e.preventDefault();
      e.stopPropagation();
      scrollToIndex(idx, 'smooth');
    }
    slides.forEach(function (slide) {
      slide.addEventListener('click', onSlideClick, true);
    });

    function scrollToIndex(i, behavior) {
      var slide = slides[i];
      if (!slide) return;
      var target = slide.offsetLeft - (scroller.clientWidth - slide.offsetWidth) / 2;
      target = Math.max(0, Math.min(target, scroller.scrollWidth - scroller.clientWidth));
      scroller.scrollTo({ left: target, behavior: behavior || 'smooth' });
    }

    function findActiveIndex() {
      var center = scroller.scrollLeft + scroller.clientWidth / 2;
      var bestI = 0;
      var bestDist = Infinity;
      // Re-read slides each time in case the underlying set changed
      // (the reviews track manages its own ‘visible’ set via the
      // first-set filter and may shift cards underneath us).
      slides.forEach(function (s, i) {
        var cardCenter = s.offsetLeft + s.offsetWidth / 2;
        var d = Math.abs(cardCenter - center);
        if (d < bestDist) {
          bestDist = d;
          bestI = i;
        }
      });
      return bestI;
    }

    var rafId = null;
    function onScroll() {
      if (rafId != null) return;
      rafId = requestAnimationFrame(function () {
        rafId = null;
        setActive(findActiveIndex());
      });
    }
    scroller.addEventListener('scroll', onScroll, { passive: true });
    setActive(findActiveIndex());

    // ---- Autoplay progress hooks ----
    // For sliders with autoplay (reviews), the active dot fills over the
    // interval. The fill resets on each new active dot.
    var fillTransitionMs = 0;
    function getActiveFill() {
      if (active < 0) return null;
      return dots[active].querySelector('.mobile-pagination__dot-fill');
    }

    function startTick(durationMs) {
      var fill = getActiveFill();
      if (!fill) return;
      fillTransitionMs = durationMs;
      // Reset all other fills first
      dots.forEach(function (d, i) {
        var f = d.querySelector('.mobile-pagination__dot-fill');
        if (!f) return;
        if (i === active) return;
        f.style.transition = 'none';
        f.style.transform = 'scaleX(0)';
      });
      // Restart the active fill
      fill.style.transition = 'none';
      fill.style.transform = 'scaleX(0)';
      // force reflow so the next transition is recognized
      void fill.offsetWidth;
      fill.style.transition = 'transform ' + durationMs + 'ms linear';
      fill.style.transform = 'scaleX(1)';
    }

    function stopTick() {
      dots.forEach(function (d) {
        var f = d.querySelector('.mobile-pagination__dot-fill');
        if (!f) return;
        f.style.transition = 'none';
        f.style.transform = 'scaleX(0)';
      });
      bar.classList.add('mobile-pagination--idle');
    }

    function resetActiveFillInstant() {
      var fill = getActiveFill();
      if (!fill) return;
      fill.style.transition = 'none';
      fill.style.transform = 'scaleX(0)';
    }

    scroller.__pagination = {
      dots: dots,
      slides: slides,
      bar: bar,
      setActive: setActive,
      scrollToIndex: scrollToIndex,
      startTick: startTick,
      stopTick: stopTick,
      resetActiveFillInstant: resetActiveFillInstant
    };

    // Hide on desktop / when only one card
    var update = function () {
      if (mobileQuery.matches && slides.length > 1) {
        bar.style.display = '';
      } else {
        bar.style.display = 'none';
      }
    };
    update();
    mobileQuery.addEventListener ? mobileQuery.addEventListener('change', update) : mobileQuery.addListener(update);
  }

  function init() {
    document.querySelectorAll('[data-mobile-pagination]').forEach(build);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify theme editor section reload
  document.addEventListener('shopify:section:load', init);
})();
